import React, { useEffect, useMemo, useState, useContext } from "react";
import CmsContext from "../../store/cms-context";
import { getAccessJwtToken } from "../../store/globalUtilities";

// MUI
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
} from "@mui/material";

const CUSTOMERS_API_BASE =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/overview";

// ---------- Helpers ----------
function parseDate(d) {
  const date = new Date(d);
  if (isNaN(date.getTime())) throw new Error(`Invalid date: ${d}`);
  return date;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function floorToInterval(date, groupBy) {
  const d = new Date(date);
  switch (groupBy) {
    case "hour":
      d.setUTCMinutes(0, 0, 0);
      break;
    case "day":
      d.setUTCHours(0, 0, 0, 0);
      break;
    case "week":
      return startOfWeek(d);
    case "month":
      d.setUTCDate(1);
      d.setUTCHours(0, 0, 0, 0);
      break;
    default:
      break;
  }
  return d;
}

function formatTick(ts, groupBy) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  switch (groupBy) {
    case "hour":
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
        d.getUTCDate()
      )} ${pad(d.getUTCHours())}:00`;
    case "day":
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
        d.getUTCDate()
      )}`;
    case "week":
      return `Week of ${d.getUTCFullYear()}-${pad(
        d.getUTCMonth() + 1
      )}-${pad(d.getUTCDate())}`;
    case "month":
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
    default:
      return d.toISOString();
  }
}

function toBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return v.toLowerCase() === "true" || v === "1";
  return false;
}

function normalizeApiData(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.map(coerceRow);
  if (payload && Array.isArray(payload.columns) && Array.isArray(payload.data)) {
    const { columns, data } = payload;
    return data.map((row) => {
      const obj = {};
      columns.forEach((col, i) => (obj[col] = row[i]));
      return coerceRow(obj);
    });
  }
  console.warn("normalizeApiData: unknown payload shape", payload);
  return [];
}

function coerceRow(r) {
  return {
    customer_id: String(r.customer_id ?? ""),
    first_name: r.first_name ?? null,
    created_at: r.created_at,
    store_id: r.store_id ?? null,
    country_code: r.country_code ?? null,
    phone_number: r.phone_number != null ? String(r.phone_number) : null,
    whatsapp_consent: toBool(r.whatsapp_consent),
  };
}

function buildTimeSeries(rows, groupBy, onlyWithConsent) {
  const buckets = new Map();
  for (const r of rows) {
    const consent = r.whatsapp_consent === true;
    if (onlyWithConsent && !consent) continue;
    const created = parseDate(r.created_at);
    const floored = floorToInterval(created, groupBy);
    const key = floored.getTime();
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([ts, count]) => ({ ts, label: formatTick(ts, groupBy), count }));
}

// ---------- Interactive SVG Line Chart ----------
// --- Interactive Chart (fixed hooks order / safe init) ---
function SimpleLineChart({ data, height = 320 }) {
  // Drag to zoom, Shift+Drag to pan, Wheel to zoom at cursor, Double-click to reset
  const padding = { top: 16, right: 16, bottom: 44, left: 64 };
  const width = 900;

  // Always initialize with a stable default; update from data in useEffect
  const [view, setView] = useState({ start: 0, end: 1 });
  const [drag, setDrag] = useState(null); // {x0, x1, mode: 'brush'|'pan'}
  const svgRef = React.useRef(null);

  useEffect(() => {
    if (data && data.length > 0) {
      setView({ start: data[0].ts, end: data[data.length - 1].ts });
    }
  }, [data]);

  // If no data, render nothing (after hooks have been declared)
  if (!data || data.length === 0) return null;

  const maxY = Math.max(1, ...data.map((d) => d.count));
  const minData = data[0].ts;
  const maxData = data[data.length - 1].ts;

  // Clamp the view to data extent to avoid NaNs
  const xMin = Math.max(minData, Math.min(view.start, view.end));
  const xMax = Math.min(maxData, Math.max(view.start, view.end));
  const xSpan = Math.max(1, xMax - xMin);

  const toSvgX = (ts) =>
    padding.left + ((ts - xMin) / xSpan) * (width - padding.left - padding.right);
  const toSvgY = (v) =>
    padding.top + (1 - v) * (height - padding.top - padding.bottom);
  const fromSvgXToTs = (svgX) =>
    xMin + ((svgX - padding.left) / (width - padding.left - padding.right)) * xSpan;

  // Visible points (keep original labels)
  const points = data
    .filter((d) => d.ts >= xMin && d.ts <= xMax)
    .map((d) => ({ ...d, y: d.count / maxY }));

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${toSvgX(p.ts)},${toSvgY(p.y)}`)
    .join(" ");

  // Y ticks
  const yTicks = 4;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round((i * maxY) / yTicks)
  );

  // X ticks (sample visible points)
  const tickCount = Math.min(8, Math.max(2, points.length));
  const step = Math.max(1, Math.ceil(points.length / tickCount));
  const xTickIdxs = new Set([0, Math.max(0, points.length - 1)]);
  for (let i = 0; i < points.length; i += step) xTickIdxs.add(i);

  // Interactions
  const onMouseDown = (e) => {
    const bbox = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bbox.left;
    const inside = x >= padding.left && x <= width - padding.right;
    if (!inside) return;
    const mode = e.shiftKey ? "pan" : "brush";
    setDrag({ x0: x, x1: x, mode });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    const bbox = e.currentTarget.getBoundingClientRect();
    const x = Math.max(
      padding.left,
      Math.min(width - padding.right, e.clientX - bbox.left)
    );
    setDrag((d) => ({ ...d, x1: x }));
  };

  const onMouseUp = () => {
    if (!drag) return;
    if (drag.mode === "brush") {
      const x0 = Math.min(drag.x0, drag.x1);
      const x1 = Math.max(drag.x0, drag.x1);
      if (x1 - x0 > 6) {
        const ts0 = fromSvgXToTs(x0);
        const ts1 = fromSvgXToTs(x1);
        const start = Math.max(minData, Math.min(ts0, ts1));
        const end = Math.min(maxData, Math.max(ts0, ts1));
        setView({ start, end });
      }
    } else if (drag.mode === "pan") {
      const dx = drag.x1 - drag.x0;
      const dt = (dx / (width - padding.left - padding.right)) * xSpan;
      let start = xMin - dt;
      let end = xMax - dt;
      const span = end - start;
      if (start < minData) {
        start = minData;
        end = start + span;
      }
      if (end > maxData) {
        end = maxData;
        start = end - span;
      }
      setView({ start, end });
    }
    setDrag(null);
  };

  const onDoubleClick = () => {
    setView({ start: minData, end: maxData });
  };

  const onWheel = (e) => {
    e.preventDefault();
    const bbox = e.currentTarget.getBoundingClientRect();
    const svgX = e.clientX - bbox.left;
    const centerTs = fromSvgXToTs(svgX);
    const zoomFactor = e.deltaY < 0 ? 0.85 : 1.15; // in/out
    const newSpan = Math.max(1, xSpan * zoomFactor);
    let start =
      centerTs -
      ((svgX - padding.left) / (width - padding.left - padding.right)) * newSpan;
    let end = start + newSpan;
    if (start < minData) {
      start = minData;
      end = start + newSpan;
    }
    if (end > maxData) {
      end = maxData;
      start = end - newSpan;
    }
    setView({ start, end });
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto", userSelect: "none" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onDoubleClick={onDoubleClick}
      >
        {/* Y grid + ticks */}
        {yTickVals.map((v, i) => {
          const y = toSvgY(v / maxY);
          return (
            <g key={i}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="#eee" />
              <text x={padding.left - 12} y={y} textAnchor="end" dominantBaseline="middle" fontSize="10">{v}</text>
            </g>
          );
        })}

        {/* X axis */}
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} stroke="#ccc" />
        {[...xTickIdxs].sort((a,b)=>a-b).map((idx) => {
          const p = points[idx];
          if (!p) return null;
          const x = toSvgX(p.ts);
          const y = height - padding.bottom;
          return (
            <g key={`xt-${idx}`}>
              <line x1={x} x2={x} y1={y} y2={y + 4} stroke="#ccc" />
              <text x={x} y={y + 14} textAnchor="middle" fontSize="10">
                {p.label}
              </text>
            </g>
          );
        })}

        {/* Line */}
        <path d={pathD} fill="none" stroke="#1976d2" strokeWidth="2" />

        {/* Dots + native tooltip */}
        {points.map((p, i) => (
          <circle key={i} cx={toSvgX(p.ts)} cy={toSvgY(p.y)} r={3} fill="#1976d2">
            <title>{`${p.count} signups\n${p.label}`}</title>
          </circle>
        ))}

        {/* Rotated Y-axis title */}
        <text transform="rotate(-90)" x={-height / 2} y={16} textAnchor="middle" fontSize="12" fill="#333">
          Number of Sign-Ups
        </text>

        {/* Brush rectangle */}
        {drag && drag.mode === "brush" && (
          <rect
            x={Math.min(drag.x0, drag.x1)}
            y={padding.top}
            width={Math.abs(drag.x1 - drag.x0)}
            height={height - padding.top - padding.bottom}
            fill="#1976d2"
            opacity={0.1}
            stroke="#1976d2"
            strokeDasharray="4 2"
          />
        )}
      </svg>
    </Box>
  );
}

// ---------- Component ----------
export default function WhatsAppOverview() {
  const cmsContext = useContext(CmsContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);

  const [groupBy, setGroupBy] = useState("day");
  const [onlyWithConsent, setOnlyWithConsent] = useState(false);

  // Date filters (date-only for simplicity; inclusive end-of-day)
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const auth = await getAccessJwtToken();
        const email = cmsContext?.current_shop?.email;
        const res = await fetch(CUSTOMERS_API_BASE, {
          method: "GET",
          headers: { Authorization: auth.jwt, email },
        });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const raw = await res.json();
        const normalized = normalizeApiData(raw);
        if (!cancelled) setCustomers(normalized);
      } catch (e) {
        console.error("Customers fetch error", e);
        if (!cancelled) setError("Failed to load customers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cmsContext]);

  const totals = useMemo(() => {
    const total = customers.length;
    const withConsent = customers.filter((c) => c.whatsapp_consent === true).length;
    const withoutConsent = total - withConsent;
    return { total, withConsent, withoutConsent };
  }, [customers]);

  // Build chart series directly from customers + filters (keeps ESLint happy)
  const chartData = useMemo(() => {
    let rows = customers;

    if (dateStart) {
      const s = new Date(dateStart);
      rows = rows.filter((c) => new Date(c.created_at) >= s);
    }
    if (dateEnd) {
      const e = new Date(dateEnd);
      e.setUTCHours(23, 59, 59, 999);
      rows = rows.filter((c) => new Date(c.created_at) <= e);
    }

    return buildTimeSeries(rows, groupBy, onlyWithConsent);
  }, [customers, dateStart, dateEnd, groupBy, onlyWithConsent]);

  // Table rows: same date filters + optional consent + sort by newest
  const tableRows = useMemo(() => {
    let rows = onlyWithConsent
      ? customers.filter((c) => c.whatsapp_consent)
      : customers;

    const s = dateStart ? new Date(dateStart) : null;
    const e = dateEnd ? new Date(dateEnd) : null;
    if (e) e.setUTCHours(23, 59, 59, 999);

    rows = rows.filter((c) => {
      const created = new Date(c.created_at);
      if (s && created < s) return false;
      if (e && created > e) return false;
      return true;
    });

    return rows.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [customers, onlyWithConsent, dateStart, dateEnd]);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        WhatsApp Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        WhatsApp sign-ups and growth over time.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Customers
                </Typography>
                <Typography variant="h4">
                  {totals.total.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All customers
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  With WhatsApp consent
                </Typography>
                <Typography variant="h4">
                  {totals.withConsent.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Customers opted in
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Without WhatsApp consent
                </Typography>
                <Typography variant="h4">
                  {totals.withoutConsent.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Customers not opted in or failed
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Controls + Chart */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="groupby-label">Group by</InputLabel>
                  <Select
                    labelId="groupby-label"
                    label="Group by"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <MenuItem value="hour">Hour</MenuItem>
                    <MenuItem value="day">Day</MenuItem>
                    <MenuItem value="week">Week</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  sx={{ ml: 2 }}
                  control={
                    <Switch
                      checked={onlyWithConsent}
                      onChange={(e) => setOnlyWithConsent(e.target.checked)}
                    />
                  }
                  label="Only WhatsApp consent = true"
                />
              </Grid>

              {/* Date range filters */}
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box mt={2}>
              <SimpleLineChart data={chartData} />
            </Box>
          </Paper>

          {/* Table */}
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle1">Customers</Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Store ID</TableCell>
                    <TableCell>Country Code</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>WhatsApp Consent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRows.map((c) => (
                      <TableRow key={c.customer_id} hover>
                        <TableCell>{c.customer_id}</TableCell>
                        <TableCell>{c.first_name ?? "—"}</TableCell>
                        <TableCell>{c.created_at}</TableCell>
                        <TableCell>{c.store_id ?? "—"}</TableCell>
                        <TableCell>{c.country_code ?? "—"}</TableCell>
                        <TableCell>{c.phone_number ?? "—"}</TableCell>
                        <TableCell>{c.whatsapp_consent ? "True" : "False"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
